/**
 * StakeWise Protocol Implementation
 *
 * @remarks
 * Concrete implementation of the StakingProtocol for StakeWise liquid staking.
 * Provides comprehensive staking functionality including deposits, withdrawals,
 * rewards management, and vault operations.
 *
 * @internal
 * @packageDocumentation
 */
import { Network, StakeWiseSDK } from "@stakewise/v3-sdk";
import { normalizeChainIdString } from "../../../../shared/caip2";
import { executeStakewiseOperation } from "../../../../shared/executor";
import { StakingProtocol } from "../../staking-protocol";
import { resolveVaultAddress } from "./internal/util";
import { _convertVaultAssetsToShares, _getVault } from "./read";
import { _claimRewards, _estimateClaimRewardsGas, _getStakeBalance, _getStakerActions, _getUserRewards, } from "./rewards";
import { _buildStakeTx, _claimExitQueueTx, _estimateDepositGas, _withdrawTx } from "./transactions";
import { _getExitQueuePositions, _getMaxWithdraw } from "./withdraw";
// All implementations are now directly in this protocol class
/**
 * StakeWise Protocol Implementation
 *
 * @remarks
 * Provides liquid staking functionality through the StakeWise protocol.
 * Supports Ethereum mainnet and Hoodi testnet with comprehensive staking operations.
 *
 * @example
 * ```typescript
 * const stakewise = new StakewiseProtocol({
 *   logger: myLogger,
 *   name: "StakeWise",
 *   availableChains: ["eip155:1", "eip155:560048"],
 *   chainService: myChainService
 * });
 * ```
 */
class StakewiseProtocol extends StakingProtocol {
    stakewiseSDKMap = new Map();
    // ========== VAULT OPERATIONS ==========
    /**
     * Retrieves comprehensive vault information and metrics.
     *
     * @remarks
     * Gets vault configuration, performance data, and operational metrics
     * including APY, capacity, fees, and token information.
     *
     * @param request - Vault request with chain identifier and optional `operatorAddress`
     *   (StakeWise vault contract address). When omitted, the default vault for the chain is used.
     * @returns Promise resolving to complete vault information
     *
     * @see {@link https://sdk.stakewise.io/vault/requests/getvault} StakeWise SDK getVault Documentation
     *
     * @example
     * ```typescript
     * const vault = await stakewise.getVault({
     *   chainId: "eip155:1"
     * });
     *
     * console.log(`Vault APY: ${vault.apy}%`);
     * console.log(`Available Capacity: ${vault.capacity} ETH`);
     * console.log(`Total Assets: ${vault.totalAssets} wei`);
     * ```
     */
    async getVault(request) {
        const chainId = normalizeChainIdString(request.chainId);
        if (!chainId) {
            throw new Error(`Invalid chain ID: ${request.chainId}`);
        }
        const sdk = await this.getStakeWiseSDK(chainId);
        const vaultAddress = resolveVaultAddress(chainId, request.operatorAddress?.trim(), this.protocolConfig?.ethereum);
        return executeStakewiseOperation("getVault", this.logger, _getVault(sdk, this.logger, vaultAddress));
    }
    // ========== STAKING OPERATIONS ==========
    /**
     * Builds a staking transaction for depositing ETH into the vault.
     *
     * @remarks
     * Creates a transaction to stake ETH and receive liquid staking tokens.
     * The transaction can be signed and broadcast to complete the staking process.
     *
     * @param request - Staking request with amount and user details
     * @returns Promise resolving to transaction data ready for signing
     *
     * @see {@link https://sdk.stakewise.io/vault/transactions/deposit} StakeWise SDK deposit Documentation
     *
     * @example
     * ```typescript
     * const stakeTx = await stakewise.buildStakeTx({
     *   assets: BigInt("1000000000000000000"), // 1 ETH in wei
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "eip155:1"
     * });
     *
     * // Sign and broadcast the transaction
     * const receipt = await wallet.sendTransaction({
     *   to: stakeTx.to,
     *   data: stakeTx.data,
     *   value: stakeTx.value
     * });
     * ```
     */
    async buildStakeTx(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("buildStakeTx", this.logger, _buildStakeTx(stakewiseSDK, this.logger, request, this.referralTag));
    }
    /**
     * Estimates gas cost for a staking deposit transaction.
     *
     * @remarks
     * Provides accurate gas estimation for staking transactions to help
     * users calculate transaction costs before execution.
     *
     * @param request - Staking request parameters for gas estimation
     * @returns Promise resolving to estimated gas units
     *
     * @see {@link https://sdk.stakewise.io/vault/transactions/deposit} StakeWise SDK deposit Documentation
     *
     * @example
     * ```typescript
     * const gasEstimate = await stakewise.estimateDepositGas({
     *   assets: BigInt("1000000000000000000"), // 1 ETH
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "eip155:1"
     * });
     *
     * const gasPrice = BigInt("20000000000"); // 20 gwei
     * const totalCost = gasEstimate * gasPrice;
     * console.log(`Estimated cost: ${Number(totalCost) / 1e18} ETH`);
     * ```
     */
    async estimateDepositGas(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("estimateDepositGas", this.logger, _estimateDepositGas(stakewiseSDK, this.logger, request, this.referralTag));
    }
    // ========== WITHDRAWAL OPERATIONS ==========
    /**
     * Builds a withdrawal transaction for claiming unstaked assets.
     *
     * @remarks
     * Creates a transaction to claim assets that have completed the exit queue process.
     * Use after unstaking when positions become claimable.
     *
     * @param request - Withdrawal request with amount and user details
     * @returns Promise resolving to withdrawal transaction data
     *
     * @see {@link https://sdk.stakewise.io/vault/transactions/withdraw} StakeWise SDK withdraw Documentation
     *
     * @example
     * ```typescript
     * const withdrawTx = await stakewise.buildWithdrawTx({
     *   amount: BigInt("500000000000000000"), // 0.5 ETH
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "eip155:1"
     * });
     *
     * // Sign and broadcast to claim assets
     * await wallet.sendTransaction({
     *   to: withdrawTx.to,
     *   data: withdrawTx.data,
     *   value: withdrawTx.value // Usually 0 for withdrawals
     * });
     * ```
     */
    async buildWithdrawTx(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("buildWithdrawTx", this.logger, _withdrawTx(stakewiseSDK, this.logger, {
            assets: request.amount,
            userAddress: request.userAddress,
            vaultAddress: request.vaultAddress,
            chainId: request.chainId,
        }, this.referralTag));
    }
    /**
     * Calculates the maximum withdrawable amount for a user.
     *
     * @remarks
     * Determines how much a user can withdraw while maintaining required
     * collateralization for any minted osTokens. Accounts for vault liquidity.
     *
     * @param request - Request with LTV and asset parameters
     * @returns Promise resolving to maximum withdrawable amount in wei
     *
     * @see {@link https://sdk.stakewise.io/vault/requests/getmaxwithdraw} StakeWise SDK getMaxWithdraw Documentation
     * @see {@link https://sdk.stakewise.io/osToken/requests/getposition} StakeWise SDK osToken getPosition Documentation
     *
     * @example
     * ```typescript
     * const maxWithdraw = await stakewise.getMaxWithdraw({
     *   ltvPercent: 9000n, // 90% LTV
     *   mintedAssets: BigInt("1000000000000000000"), // 1 osETH minted
     *   stakedAssets: BigInt("2000000000000000000"), // 2 ETH staked
     *   chainId: "eip155:1"
     * });
     *
     * console.log(`Can withdraw up to: ${Number(maxWithdraw) / 1e18} ETH`);
     * ```
     */
    async getMaxWithdraw(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("getMaxWithdraw", this.logger, _getMaxWithdraw(stakewiseSDK, this.logger, request));
    }
    /**
     * Previews vault shares for a native asset amount via on-chain `convertToShares`.
     *
     * @remarks
     * Uses the StakeWise SDK vault contract (same rounding as deposits). For dust
     * amounts the returned `shares` may be `0n`.
     *
     * @param request - `assets` in wei, optional `vaultAddress`, and `chainId`
     * @returns Share amount as `bigint`
     */
    async convertVaultAssetsToShares(request) {
        const chainId = normalizeChainIdString(request.chainId);
        if (!chainId) {
            throw new Error(`Invalid chain ID: ${request.chainId}`);
        }
        if (!this.availableChains.includes(chainId)) {
            throw new Error(`Chain ${chainId} is not supported by ${this.name} protocol`);
        }
        if (request.assets < 0n) {
            throw new Error("assets must be non-negative");
        }
        const stakewiseSDK = await this.getStakeWiseSDK(chainId);
        const vaultAddress = resolveVaultAddress(chainId, request.vaultAddress?.trim(), this.protocolConfig?.ethereum);
        const shares = await executeStakewiseOperation("convertVaultAssetsToShares", this.logger, _convertVaultAssetsToShares(stakewiseSDK, this.logger, vaultAddress, request.assets));
        return { shares };
    }
    /**
     * Retrieves exit queue positions for tracking withdrawal progress.
     *
     * @remarks
     * Shows user's positions in the exit queue from previous unstake operations.
     * Indicates which positions are claimable and their processing status.
     * Returns withdrawal queue data including duration estimates and withdrawable amounts.
     *
     * @param request - Request with user and vault information
     * @returns Promise resolving to exit queue positions data
     *
     * @see {@link https://sdk.stakewise.io/vault/requests/getexitqueuepositions} StakeWise SDK getExitQueuePositions Documentation
     * @see {@link https://sdk.stakewise.io/boost/requests/getqueueposition} StakeWise SDK boost getQueuePosition Documentation
     *
     * @example
     * ```typescript
     * const positions = await stakewise.getExitQueuePositions({
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "eip155:1"
     * });
     *
     * console.log(`Total positions: ${positions.positions.length}`);
     * console.log(`Withdrawable amount: ${Number(positions.withdrawable) / 1e18} ETH`);
     * console.log(`Queue duration: ${positions.duration} seconds`);
     *
     * positions.positions.forEach((pos, i) => {
     *   console.log(`Position ${i + 1}: ${pos.assets} assets, claimable: ${pos.isClaimable}`);
     * });
     * ```
     */
    async getExitQueuePositions(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("getExitQueuePositions", this.logger, _getExitQueuePositions(stakewiseSDK, this.logger, request));
    }
    /**
     * Builds a transaction to claim assets from completed exit queue positions.
     *
     * @remarks
     * Creates a transaction to claim one or more exit queue positions that have
     * completed processing. Can batch multiple positions for efficiency.
     * Uses positions data from getExitQueuePositions for claiming.
     *
     * @param request - Claim request with exit queue positions
     * @returns Promise resolving to claim transaction data
     *
     * @see {@link https://sdk.stakewise.io/vault/transactions/claimexitqueue} StakeWise SDK claimExitQueue Documentation
     * @see {@link https://sdk.stakewise.io/vault/requests/getexitqueuepositions} StakeWise SDK getExitQueuePositions Documentation
     *
     * @example
     * ```typescript
     * // First get claimable positions
     * const exitQueue = await stakewise.getExitQueuePositions({
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "eip155:1"
     * });
     *
     * const claimablePositions = exitQueue.positions.filter(p => p.isClaimable);
     *
     * if (claimablePositions.length > 0) {
     *   const claimTx = await stakewise.claimExitQueueTx({
     *     positions: claimablePositions,
     *     userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *     chainId: "eip155:1"
     *   });
     *
     *   // Execute the claim transaction
     *   await wallet.sendTransaction(claimTx);
     * }
     * ```
     */
    async claimExitQueueTx(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("claimExitQueueTx", this.logger, _claimExitQueueTx(stakewiseSDK, this.logger, request, this.referralTag));
    }
    // ========== BALANCE AND POSITION OPERATIONS ==========
    /**
     * Retrieves the current stake balance for a user.
     *
     * @remarks
     * Gets user's current staked assets and total earned rewards.
     * Reflects real-time vault performance and user's position.
     *
     * @param request - Balance request with user and vault information
     * @returns Promise resolving to current stake balance
     *
     * @see {@link https://sdk.stakewise.io/vault/requests/getstakebalance} StakeWise SDK getStakeBalance Documentation
     *
     * @example
     * ```typescript
     * const balance = await stakewise.getStakeBalance({
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "eip155:1"
     * });
     *
     * const currentETH = Number(balance.assets) / 1e18;
     * const earnedETH = Number(balance.totalEarnedAssets) / 1e18;
     * const stakeEarnedETH = Number((balance.metadata as { totalStakeEarnedAssets: bigint }).totalStakeEarnedAssets) / 1e18;
     *
     * console.log(`Token: ${balance.asset.symbol}`); // "ETH"
     * console.log(`Current Balance: ${currentETH.toFixed(6)} ETH`);
     * console.log(`Total Earned: ${earnedETH.toFixed(6)} ETH`);
     * console.log(`Staking Rewards: ${stakeEarnedETH.toFixed(6)} ETH`);
     * ```
     */
    async getStakeBalance(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("getStakeBalance", this.logger, _getStakeBalance(stakewiseSDK, this.logger, request));
    }
    /**
     * Retrieves staker actions and transaction history.
     *
     * @remarks
     * Gets chronological list of staking-related actions including deposits,
     * withdrawals, claims, and other vault interactions with pagination support.
     *
     * @param request - Actions request with filtering and pagination options
     * @returns Promise resolving to array of staker actions
     *
     * @see {@link https://sdk.stakewise.io/vault/requests/getstakeractions} StakeWise SDK getStakerActions Documentation
     *
     * @example
     * ```typescript
     * const actions = await stakewise.getStakerActions({
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   types: ["deposit", "withdraw"], // Optional filter
     *   limit: 20,
     *   skip: 0,
     *   chainId: "eip155:1"
     * });
     *
     * actions.forEach(action => {
     *   console.log(`${action.actionType}: ${action.assets} assets on ${new Date(action.createdAt * 1000)}`);
     *   console.log(`Transaction: ${action.link}`);
     * });
     * ```
     */
    async getStakerActions(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("getStakerActions", this.logger, _getStakerActions(stakewiseSDK, this.logger, request));
    }
    // ========== REWARDS OPERATIONS ==========
    /**
     * Retrieves user rewards history over a specified time period.
     *
     * @remarks
     * Gets daily reward breakdowns with multi-currency support (ETH, USD, EUR).
     * Useful for reward tracking, tax reporting, and performance analysis.
     *
     * @param request - Rewards request with date range and user information
     * @returns Promise resolving to array of daily rewards data
     *
     * @see {@link https://sdk.stakewise.io/vault/requests/getuserrewards} StakeWise SDK getUserRewards Documentation
     *
     * @example
     * ```typescript
     * const rewards = await stakewise.getUserRewards({
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   dateFrom: "2024-01-01",
     *   dateTo: "2024-01-31",
     *   chainId: "eip155:1"
     * });
     *
     * const monthlyETH = rewards.reduce((sum, day) =>
     *   sum + parseFloat(day.dailyRewards), 0
     * );
     *
     * console.log(`January Rewards: ${monthlyETH.toFixed(6)} ETH`);
     * ```
     */
    async getUserRewards(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("getUserRewards", this.logger, _getUserRewards(stakewiseSDK, this.logger, request));
    }
    /**
     * Claims accumulated rewards for a user.
     *
     * @remarks
     * Builds a transaction to claim staking rewards. Requires reward splitter
     * configuration and sufficient accumulated rewards.
     *
     * @param request - Claim rewards request with amount and addresses
     * @returns Promise resolving to claim transaction data
     *
     * @example
     * ```typescript
     * const claimTx = await stakewise.claimRewards({
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   rewardSplitterAddress: "0x...", // Reward splitter contract
     *   assets: BigInt("100000000000000000"), // 0.1 ETH worth of rewards
     *   chainId: "eip155:1"
     * });
     *
     * // Execute the claim transaction
     * const receipt = await wallet.sendTransaction({
     *   to: claimTx.to,
     *   data: claimTx.data,
     *   value: claimTx.value
     * });
     * ```
     */
    async claimRewards(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("claimRewards", this.logger, _claimRewards(stakewiseSDK, this.logger, request));
    }
    /**
     * Estimates gas cost for claiming rewards.
     *
     * @remarks
     * Provides gas estimation for reward claiming transactions to help
     * users determine if claiming is cost-effective.
     *
     * @param request - Claim rewards request for gas estimation
     * @returns Promise resolving to estimated gas units
     *
     * @example
     * ```typescript
     * const gasEstimate = await stakewise.estimateClaimRewardsGas({
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   rewardSplitterAddress: "0x...",
     *   assets: BigInt("100000000000000000"), // 0.1 ETH
     *   chainId: "eip155:1"
     * });
     *
     * const gasPrice = BigInt("20000000000"); // 20 gwei
     * const gasCost = Number(gasEstimate * gasPrice) / 1e18;
     * const rewardValue = 0.1; // ETH
     *
     * if (gasCost < rewardValue * 0.1) { // Less than 10% of reward value
     *   console.log("Claiming is profitable!");
     * }
     * ```
     */
    async estimateClaimRewardsGas(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        const stakewiseSDK = await this.getStakeWiseSDK(request.chainId);
        return executeStakewiseOperation("estimateClaimRewardsGas", this.logger, _estimateClaimRewardsGas(stakewiseSDK, this.logger, request));
    }
    // ========== PREPARE TO WITHDRAW OPERATIONS ==========
    /**
     * Prepares an active stake position for withdrawal.
     *
     * @remarks
     * TBD rename claimexitqueue to preparetoexitqueue
     */
    async prepareToWithdraw(_request) {
        throw new Error("StakeWise prepareToWithdraw not implemented - use buildWithdrawTx for unstaking");
    }
    /**
     * Retrieves StakeWise vault information as a unified staking metadata response.
     *
     * @remarks
     * Delegates to {@link getVault} and maps the result into
     * {@link IGetStakingMetadataResponse}. When `operatorAddress` is omitted or
     * blank, the default vault for the chain is used (see {@link VAULT_ADDRESSES_MAP}).
     * When set, it must be a StakeWise vault address — the SDK returns that vault's
     * `displayName`, `imageUrl`, APY, fees, and related fields (same resolution
     * pattern as Solana's optional validator `operatorAddress`).
     */
    async getStakingMetadata(request) {
        const vault = await this.getVault({
            chainId: request.chainId,
            operatorAddress: request.operatorAddress,
        });
        return {
            operatorAddress: vault.vaultAddress,
            protocol: this.name,
            apyPercentage: vault.apy,
            feePercentage: vault.feePercent,
            detail: {
                type: "stakewise",
                vault,
            },
        };
    }
    // ========== HIGH-LEVEL OPERATIONS ==========
    /**
     * High-level unstake operation that handles the complete unstaking workflow.
     *
     * @remarks
     * Comprehensive unstaking operation that combines balance checking, withdrawal
     * calculation, transaction building, and exit queue management in one call.
     *
     * @param request - Unstake request with amount and user details
     * @returns Promise resolving to complete unstake result with all transaction data
     *
     * @see {@link https://sdk.stakewise.io/vault/requests/getstakebalance} StakeWise SDK getStakeBalance Documentation
     * @see {@link https://sdk.stakewise.io/vault/requests/getmaxwithdraw} StakeWise SDK getMaxWithdraw Documentation
     * @see {@link https://sdk.stakewise.io/vault/requests/getexitqueuepositions} StakeWise SDK getExitQueuePositions Documentation
     * @see {@link https://sdk.stakewise.io/vault/transactions/claimexitqueue} StakeWise SDK claimExitQueue Documentation
     *
     * @example
     * ```typescript
     * const unstakeResult = await stakewise.unstake({
     *   amount: BigInt("1000000000000000000"), // 1 ETH, or omit for max
     *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "eip155:1"
     * });
     *
     * console.log(`Current Balance: ${Number(unstakeResult.stakeBalance.assets) / 1e18} ETH`);
     * console.log(`Max Withdrawable: ${Number(unstakeResult.maxWithdraw) / 1e18} ETH`);
     *
     * // Execute the withdrawal transaction
     * await wallet.sendTransaction(unstakeResult.buildWithdrawTx);
     *
     * // If there are claimable positions, execute claim
     * if (unstakeResult.claimExitQueueTx) {
     *   await wallet.sendTransaction(unstakeResult.claimExitQueueTx);
     * }
     * ```
     */
    async unstake(request) {
        if (!this.availableChains.includes(request.chainId)) {
            throw new Error(`Chain ${request.chainId} is not supported by ${this.name} protocol`);
        }
        // This is a complex operation that combines multiple calls
        const stakeBalance = await this.getStakeBalance({
            userAddress: request.userAddress,
            vaultAddress: request.vaultAddress,
            chainId: request.chainId,
        });
        const maxWithdraw = await this.getMaxWithdraw({
            ltvPercent: BigInt(0),
            mintedAssets: BigInt(0),
            stakedAssets: stakeBalance.assets,
            vaultAddress: request.vaultAddress,
            chainId: request.chainId,
        });
        const unstakeAmount = request.amount || maxWithdraw;
        if (!unstakeAmount || unstakeAmount <= 0n) {
            throw new Error("No valid amount specified");
        }
        const buildWithdrawTxResult = await this.buildWithdrawTx({
            ...request,
            amount: unstakeAmount,
        });
        const exitQueuePositions = await this.getExitQueuePositions({
            userAddress: request.userAddress,
            vaultAddress: request.vaultAddress,
            chainId: request.chainId,
        });
        let claimExitQueueTxResult;
        if (exitQueuePositions.positions.length > 0) {
            claimExitQueueTxResult = await this.claimExitQueueTx({
                positions: exitQueuePositions.positions,
                userAddress: request.userAddress,
                vaultAddress: request.vaultAddress,
                chainId: request.chainId,
            });
        }
        return {
            stakeBalance,
            maxWithdraw,
            buildWithdrawTx: buildWithdrawTxResult,
            exitQueuePositions,
            claimExitQueueTx: claimExitQueueTxResult,
        };
    }
    // ========== PRIVATE HELPER METHODS ==========
    /**
     * Gets the appropriate StakeWise SDK instance based on chain ID.
     * Creates the SDK instance lazily if it doesn't exist.
     * @private
     */
    async getStakeWiseSDK(chainId) {
        if (this.stakewiseSDKMap.has(chainId)) {
            const existingSDK = this.stakewiseSDKMap.get(chainId);
            if (existingSDK) {
                return existingSDK;
            }
        }
        this.logger.debug("Creating StakeWise SDK instance for chain", { chainId });
        const network = this.getStakeWiseNetwork(chainId);
        const provider = await this.chainService.getRpcProvider(chainId);
        const sdk = new StakeWiseSDK({
            network,
            provider: provider,
        });
        this.stakewiseSDKMap.set(chainId, sdk);
        this.logger.debug("Successfully created and cached StakeWise SDK", {
            chainId,
            network,
        });
        return sdk;
    }
    getStakeWiseNetwork(chainId) {
        switch (chainId) {
            case "eip155:1":
                return Network.Mainnet;
            case "eip155:560048":
                return Network.Hoodi;
            default:
                throw new Error(`Unsupported chain ID for StakeWise: ${chainId}`);
        }
    }
}
export { StakewiseProtocol };
//# sourceMappingURL=data:application/json;base64,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